import { MinsterCardComponent } from '../ui-components/minster-card/minster-card.component';
import { UpdateGuid } from '../../models/guid-validation-response.model';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { Customer } from '../../models/customer.model';
import { Component, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { ActivatedRoute } from '@angular/router';
import { Claim } from '../../models/claim.model';
import { CommonModule } from '@angular/common';
import { Observable, throwError } from 'rxjs';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.css',
    standalone: true,
    imports: [MatCardModule, MatButtonModule, MinsterCardComponent, CommonModule],
})

export class HomeComponent implements OnInit {
    isLoggedIn: boolean | undefined;
    _claim?: Claim;
    _customer?: Customer;
    claimReference: string | null = null;

    constructor(
        private stateService: StateService,
        private claimsService: ClaimsService,
        private authService: MsalService,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void { 
        const account = this.authService.instance.getAllAccounts()[0]; 
        this.isLoggedIn = !!account;
        this.claimReference = this.route.snapshot.paramMap.get('claimReference'); 

        if (!this.isLoggedIn) { 
            return;
        }

        if (!this.claimReference) {
            console.error("No claim reference provided in the URL.");
            return;
        }

        if (this.stateService.claim && this.stateService.customer) {
            this._claim = this.stateService.claim;
            this._customer = this.stateService.customer;

            if (this.stateService.isInitialLogin) {
                this.stateService.isInitialLogin = false;
                this.updateGuidOnLogin();
                this.initializeDropDowns();
            }
        }
        else {
            this.loadCase(this.claimReference).subscribe({
                next: () => {
                    this._claim = this.stateService.claim;
                    this._customer = this.stateService.customer;

                    if (this.stateService.isInitialLogin) {
                        this.stateService.isInitialLogin = false;
                        this.updateGuidOnLogin();
                        this.initializeDropDowns();
                    }
                },
                error: (error) => {
                    console.error("Error during case loading:", error);
                }
            });
        }
    }
    
    private updateGuidOnLogin(): void {
        if (this._claim && this._customer) {
            this.claimsService.loadGuid(this._claim.claimReference).subscribe({
                next: (existingGuidDetails) => {

                    this.stateService.formIsSubmitted = existingGuidDetails.dataComplete || false;

                    var currentDate = new Date();

                    if (!existingGuidDetails.isActiveAccount) {
                        const redirectUrl = `/${existingGuidDetails.urlGuid}/login`;
                        this.updateLogoutState(true);
                        this.authService.logoutRedirect({ postLogoutRedirectUri: redirectUrl });
                        return;
                    }

                    const updateGuidPayload: UpdateGuid = {
                        customerId: this._customer!.customerId,
                        claimId: this._claim!.claimDetailId,
                        digitalOnboardingUrlExpiry: this.calculateExpiryDate(),
                        isRegistered: true,
                        registeredDateTime: existingGuidDetails.isRegistered ? existingGuidDetails.registeredDateTime : currentDate.toISOString(),
                        isLinkAccessed: existingGuidDetails.isLinkAccessed,
                        linkAccessedDateTime: existingGuidDetails.linkAccessedDateTime,
                        dataComplete: existingGuidDetails.dataComplete,
                        dataCompleteDateTime: existingGuidDetails.dataCompleteDateTime,
                        isActiveAccount: existingGuidDetails.isActiveAccount,
                        accountClosureDateTime: existingGuidDetails.accountClosureDateTime,
                        optOutReason: existingGuidDetails.optOutReason ?? '',
                        documentSummaryId : existingGuidDetails.documentSummaryId ?? null,
                        updatedBy: this.stateService.customer?.emailAddress || this.stateService.claim?.updatedBy || 'digitalonboarding.system@minsterlaw.co.uk' 
                    };
                    
                    this.claimsService.updateGuid(updateGuidPayload).subscribe({
                        next: (response) => {
                        },
                        error: (error) => {
                            console.error('Error calling updateGuid:', error);
                        }
                    });
                },
                error: (error) => {
                    console.error('Error retrieving existing GUID details:', error);
                }
            });
        }
    }

    private calculateExpiryDate(): string {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + 30);
        return currentDate.toISOString();
    }

    private loadCase(ref: string): Observable<void> {
        return this.claimsService.loadClaim(ref).pipe(
            switchMap((claimData: Claim) => {
                if (claimData) {
                    this._claim = claimData;
                    this.stateService.claim = claimData;

                    return this.claimsService.loadCustomer(claimData.claimCustomer.customerId).pipe(
                        tap((customerData: Customer) => {
                            if (customerData) {
                                this._customer = customerData;
                                this.stateService.customer = customerData;
                            } 
                            else {
                                console.error("Customer data is undefined.");
                            }
                        })
                    );
                }
                else {
                    console.error("Claim data is undefined.");
                    return throwError(() => new Error("Claim data is undefined."));
                }
            }),
            catchError((error) => {
                console.error("Error loading case:", error);
                return throwError(() => error);
            }),
            map(() => {
                //Returns nothing to indicate completion
            })
        );
    }

    private initializeDropDowns(): void {
        try {
            this.stateService.loadDropDowns().subscribe({
                next: (data) => {
                    if (data) {
                        this.stateService.lookUps = data;
                    } 
                    else {
                        console.error('Dropdown data is undefined.');
                    }
                },
                error: (error) => {
                    console.error('Error loading dropdowns:', error);
                }
            });
        }
        catch (exception) {
            console.error('Unexpected error during dropdown initialization:', exception);
        }
    }

    private updateLogoutState(isValidLogout: boolean): void {
        if (isValidLogout) {
            this.stateService.$loginDisplay.next(true);
            this.stateService.isSigningUp = true;
            localStorage.setItem('signingUp', JSON.stringify(true));
        } else {
            this.stateService.$loginDisplay.next(false);
            this.stateService.isSigningUp = false;
            localStorage.setItem('signingUp', JSON.stringify(false));
        }
    }
}
